import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-obsidian px-5">
      <form onSubmit={submit} className="w-full max-w-md border border-paper/15 p-10" data-testid="admin-login-form">
        <p className="flex items-baseline gap-1">
          <span className="font-serif text-4xl font-semibold text-paper">RenB</span>
          <span className="inline-block h-1.5 w-1.5 bg-progress" aria-hidden="true" />
        </p>
        <h1 className="mt-6 font-serif text-3xl font-medium text-paper">Admin CMS</h1>
        <p className="mt-2 text-[12px] text-paper/50">Sign in to manage content, leads and settings.</p>

        {error && (
          <p data-testid="admin-login-error" className="mt-6 border border-progress/50 bg-progress/10 p-3 text-[13px] text-paper">
            {error}
          </p>
        )}

        <label className="mt-8 flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-paper/50">Email</span>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            className="field on-dark"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@renbsolutions.com"
          />
        </label>
        <label className="mt-6 flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-paper/50">Password</span>
          <input
            data-testid="admin-login-password"
            type="password"
            required
            className="field on-dark"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </label>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-8 flex w-full items-center justify-center gap-2 bg-progress px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-progress-dark disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"} <ArrowRight size={15} />
        </button>
        <Link to="/" className="mt-6 inline-block text-[12px] text-paper/40 hover:text-paper">
          ← Back to website
        </Link>
      </form>
    </div>
  );
}
